<template>
    <div class="tables new-updated-design px-0 px-md-0 pt-4 pt-md-0 pb-4">
        <div v-if="isLoading || resolveSubmitting" class="loader-overlay">
            <div class="spinner"></div>
        </div>
        <b-card no-body>
            <!-- Tabs Header -->
            <b-tabs card v-model="activeTab" @input="onTabChange">
                <b-tab :title="$t('dashboard.cancellations')" active></b-tab>
                <b-tab :title="$t('dashboard.refunds')"></b-tab>
                <b-tab :title="$t('dashboard.security_deposits')"></b-tab>
            </b-tabs>

            <!-- Tab Content -->
            <div class="row mt-3">
                <div class="col-lg-12 grid-margin stretch-card properties-table">
                    <div class="container mw-100">
                        <div class="card">
                            <div class="card-header">
                                <h4 class="card-title">{{ currentTabTitle }}</h4>
                            </div>
                            <div class="card-body">

                                <div v-if="activeTab === 2" class="deposit-stats-row mb-4">
                                    <div class="row">
                                        <div class="col-md-3 col-sm-6 mb-3">
                                            <div class="deposit-stat-card">
                                                <div class="deposit-stat-label">{{ $t('dashboard.deposit_stats_total') }}</div>
                                                <div class="deposit-stat-value">{{ depositStats.total_count || 0 }}</div>
                                                <div class="deposit-stat-sub">{{ formatStatAmount(depositStats.total_amount) }}</div>
                                            </div>
                                        </div>
                                        <div class="col-md-3 col-sm-6 mb-3">
                                            <div class="deposit-stat-card">
                                                <div class="deposit-stat-label">{{ $t('dashboard.deposit_stats_held') }}</div>
                                                <div class="deposit-stat-value">{{ depositStats.held_count || 0 }}</div>
                                                <div class="deposit-stat-sub">{{ formatStatAmount(depositStats.held_amount) }}</div>
                                            </div>
                                        </div>
                                        <div class="col-md-3 col-sm-6 mb-3">
                                            <div class="deposit-stat-card">
                                                <div class="deposit-stat-label">{{ $t('dashboard.deposit_stats_disputed') }}</div>
                                                <div class="deposit-stat-value">{{ depositStats.disputed_count || 0 }}</div>
                                                <div class="deposit-stat-sub">{{ formatStatAmount(depositStats.disputed_amount) }}</div>
                                            </div>
                                        </div>
                                        <div class="col-md-3 col-sm-6 mb-3">
                                            <div class="deposit-stat-card">
                                                <div class="deposit-stat-label">{{ $t('dashboard.deposit_stats_refunded') }}</div>
                                                <div class="deposit-stat-value">{{ depositStats.refunded_count || 0 }}</div>
                                                <div class="deposit-stat-sub">{{ $t('dashboard.deposit_status') }}: {{ depositStats.resolved_count || 0 }} resolved</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4">
                                            <label class="mb-1">{{ $t('dashboard.deposit_filter_status') }}</label>
                                            <select class="form-control" v-model="depositStatusFilter" @change="onDepositStatusFilterChange">
                                                <option value="">{{ $t('dashboard.deposit_all_statuses') }}</option>
                                                <option value="held">Held</option>
                                                <option value="awaiting_release">Awaiting Release</option>
                                                <option value="host_claimed">Host Claimed</option>
                                                <option value="refund_pending">Refund Pending</option>
                                                <option value="refunded">Refunded</option>
                                                <option value="resolved">Resolved</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <!-- Vue Good Table -->
                                <vue-good-table
                                    :key="'admin-table-' + activeTab + '-' + tableRefreshKey"
                                    table-class="vgt-table custom-table"
                                    mode="remote"
                                    :columns="columns"
                                    :rows="rows"
                                    :totalRows="totalRecords"
                                    :search-options="{ enabled: true, placeholder: $t('common.search_placeholder') }"
                                    :pagination-options="{
                                        enabled: true,
                                        perPage: serverParams.perPage,
                                        perPageDropdown: [5, 10, 15, 20]
                                    }"
                                    :sort-options="{ enabled: false }"
                                    @on-page-change="onPageChange"
                                    @on-per-page-change="onPerPageChange"
                                    @on-search="onSearch">
                                    <!-- Row Template -->
                                    <template slot="table-row" slot-scope="props">
                                        <template v-if="activeTab === 0">
                                            <span v-if="props.column.field === 'name'">{{ props.row.user.name }}</span>
                                            <span v-else-if="props.column.field === 'email'">{{ props.row.user.email
                                            }}</span>
                                            <span v-else-if="props.column.field === 'mobile_phone'">{{
                                                props.row.user.mobile_phone }}</span>
                                            <span v-else-if="props.column.field === 'amount'">{{
                                                props.row.property.currency
                                            }} {{ props.row.amount
                                                }}</span>
                                            <span v-else-if="props.column.field === 'property_type'">
                                                {{ $t('property_detail.' + props.row.property.property_type) }}
                                            </span>

                                            <span v-else-if="props.column.field === 'cancellation_reason'">
                                                {{
                                                    props.row.cancellation_reason
                                                        ? props.row.cancellation_reason
                                                            .split('_')
                                                            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                                            .join(' ')
                                                        : '-'
                                                }}
                                            </span>

                                            <span v-else-if="props.column.field === 'cancellation_date_time'">
                                                {{ props.row.cancellation_date_time
                                                    ? props.row.cancellation_date_time
                                                    : '-' }}
                                            </span>
                                            <span v-else-if="props.column.field === 'action'" class="text-nowrap">
                                                <b-button @click="propertyDetail(props.row.property.id)"
                                                    class="mr-1 btn-info new-primary-outline-btn" size="sm">
                                                    {{ $t('property_detail.Property_Detail') }}
                                                </b-button>
                                                <b-button @click="bookingDetail(props.row.id)"
                                                    class="mr-1 btn-secondary new-primary-btn" size="sm">
                                                    {{ $t('property_detail.Booking_detail') }}
                                                </b-button>
                                            </span>
                                        </template>

                                        <template v-else-if="activeTab === 1">
                                            <span v-if="props.column.field === 'name'">{{ props.row.user.name }}</span>
                                            <span v-else-if="props.column.field === 'email'">{{ props.row.user.email
                                            }}</span>
                                            <span v-else-if="props.column.field === 'mobile_phone'">{{
                                                props.row.user.mobile_phone }}</span>
                                            <span v-else-if="props.column.field === 'amount'">{{ props.row.amount
                                            }}</span>
                                            <span v-else-if="props.column.field === 'property_type'">
                                                {{ $t('property_detail.' + props.row.property.property_type) }}
                                            </span>
                                            <span v-else-if="props.column.field === 'propertyType'">
                                                {{ $t('property_detail.' + props.row.propertyType) }}
                                            </span>
                                            <!-- <span v-else-if="props.column.field === 'scheduledAt'">
                                                {{ props.row.scheduledAt }}
                                            </span> -->
                                            <span v-else-if="props.column.field === 'refundType'">
                                                <!-- {{ $t('Propertis.' + props.row.refundType.replace(' ', '_')) }} -->
                                                {{ $t('Propertis.' + props.row.refundType) }}
                                            </span>

                                            <!-- <span v-else-if="props.column.field === 'refundType'">
                                                {{ props.row.refundType }}
                                            </span> -->
                                            <span v-else-if="props.column.field === 'status'">
                                                {{ $t('Propertis.' + props.row.status.charAt(0).toUpperCase() +
                                                    props.row.status.slice(1).toLowerCase()) }}
                                            </span>

                                            <!-- <span v-else-if="props.column.field === 'status'">
                                                {{ props.row.status }}
                                            </span> -->
                                        </template>

                                        <template v-else-if="activeTab === 2">
                                            <span v-if="props.column.field === 'guestName'">{{ props.row.guestName }}</span>
                                            <span v-else-if="props.column.field === 'guestEmail'">{{ props.row.guestEmail }}</span>
                                            <span v-else-if="props.column.field === 'propertyType'">
                                                {{ $t('property_detail.' + props.row.propertyType) }}
                                            </span>
                                            <span v-else-if="props.column.field === 'depositAmount'">{{ props.row.depositAmount }}</span>
                                            <span v-else-if="props.column.field === 'depositStatus'">
                                                <span :class="props.row.depositStatusClass">{{ props.row.depositStatusLabel }}</span>
                                            </span>
                                            <span v-else-if="props.column.field === 'claimWindowEnds'">{{ props.row.claimWindowEnds }}</span>
                                            <span v-else-if="props.column.field === 'claimReason'">{{ props.row.claimReason }}</span>
                                            <span v-else-if="props.column.field === 'action'" class="text-nowrap">
                                                <b-button size="sm" variant="secondary" class="mr-1" @click="bookingDetail(props.row.paymentId)">
                                                    {{ $t('property_detail.Booking_detail') }}
                                                </b-button>
                                                <b-button v-if="props.row.canResolve" size="sm" variant="primary" @click="openResolveModal(props.row)">
                                                    {{ $t('dashboard.resolve_deposit') }}
                                                </b-button>
                                            </span>
                                        </template>
                                    </template>

                                    <!-- No Data Template -->
                                    <template slot="emptystate">
                                        <div class="text-center text-muted py-2 w-100">
                                            {{ $t('common.no_data_available') }}
                                        </div>
                                    </template>
                                </vue-good-table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </b-card>

        <b-modal id="resolve-deposit-modal" ref="resolveDepositModal" hide-footer
            :title="$t('dashboard.resolve_deposit')"
            :no-close-on-backdrop="resolveSubmitting"
            :no-close-on-esc="resolveSubmitting"
            :hide-header-close="resolveSubmitting">
            <b-form @submit.prevent="submitResolveDeposit">
                <div v-if="resolveSubmitting" class="resolve-submit-status text-center mb-3">
                    <div class="spinner spinner-inline mx-auto"></div>
                    <p class="text-muted small mb-0 mt-2">{{ $t('payment.processing') }}</p>
                </div>
                <fieldset :disabled="resolveSubmitting">
                    <p><strong>{{ $t('dashboard.deposit_amount') }}:</strong> {{ resolveForm.depositAmountLabel }}</p>
                    <div v-if="resolveForm.claimReason || resolveForm.claimDescription" class="claim-details-box mb-3">
                        <p v-if="resolveForm.claimReason" class="mb-2">
                            <strong>{{ $t('payment.damage_claim_reason') }}:</strong> {{ resolveForm.claimReason }}
                        </p>
                        <p v-if="resolveForm.claimDescription" class="mb-0">
                            <strong>{{ $t('payment.damage_claim_description') }}:</strong>
                        </p>
                        <p v-if="resolveForm.claimDescription" class="claim-description-text mb-0">{{ resolveForm.claimDescription }}</p>
                    </div>
                    <b-form-group :label="$t('dashboard.guest_refund_amount')">
                        <b-form-input
                            v-model.number="resolveForm.guest_refund_amount"
                            type="number"
                            min="0"
                            :max="resolveForm.depositMaxAmount"
                            step="0.01"
                            required
                            @input="onResolveGuestAmountInput"
                        />
                    </b-form-group>
                    <b-form-group :label="$t('dashboard.host_release_amount')">
                        <b-form-input
                            v-model.number="resolveForm.host_release_amount"
                            type="number"
                            min="0"
                            :max="resolveForm.depositMaxAmount"
                            step="0.01"
                            required
                            @input="onResolveHostAmountInput"
                        />
                    </b-form-group>
                    <p v-if="resolveAmountError" class="text-danger small mb-3">{{ resolveAmountError }}</p>
                    <b-form-group :label="$t('dashboard.admin_notes')">
                        <b-form-textarea v-model="resolveForm.admin_notes" rows="3" />
                    </b-form-group>
                </fieldset>
                <div class="d-flex justify-content-end">
                    <b-button class="mr-2" variant="secondary" :disabled="resolveSubmitting"
                        @click="$refs.resolveDepositModal.hide()">{{ $t('sweetalert.cancel') }}</b-button>
                    <b-button variant="primary" type="submit" :disabled="resolveSubmitting">
                        <span v-if="resolveSubmitting" class="spinner spinner-btn mr-2"></span>
                        {{ resolveSubmitting ? $t('payment.processing') : $t('common.submit') }}
                    </b-button>
                </div>
            </b-form>
        </b-modal>
    </div>
</template>

<script>
import Vue from "vue";
import API from "@/api";
import moment from "moment";
import { debounce } from "lodash";

export default {
    name: "Spotters",
    data() {
        return {
            activeTab: 0,
            serverParams: {
                search: "",
                sort: [{ field: "id", type: "desc" }],
                page: 1,
                perPage: 10
            },
            rows: [],
            totalRecords: 0,
            isLoading: false,
            resolveSubmitting: false,
            resolveAmountError: '',
            tableRefreshKey: 0,
            resolveForm: {
                paymentId: null,
                depositAmountLabel: '',
                depositMaxAmount: 0,
                claimReason: '',
                claimDescription: '',
                guest_refund_amount: 0,
                host_release_amount: 0,
                admin_notes: '',
            },
            depositStats: {},
            depositStatusFilter: '',
        };
    },
    computed: {
        columns() {
            switch (this.activeTab) {
                case 0:
                    return [
                        { label: this.$t("booking.Guest"), field: "name" },
                        { label: this.$t("booking.Guest_Email"), field: "email" },
                        { label: this.$t("booking.Guest_Contact"), field: "mobile_phone" },
                        { label: this.$t("booking.Amount"), field: "amount" },
                        { label: this.$t("properties_list.Property_Type"), field: "property_type" },
                        { label: this.$t("dashboard.cancellation_reason"), field: "cancellation_reason" },
                        { label: this.$t("dashboard.cancellation_date"), field: "cancellation_date_time" },
                        { label: this.$t("properties_list.action"), field: "action" }
                    ];
                case 1:
                    return [
                        { label: this.$t("booking.Guest"), field: "guestName" },
                        { label: this.$t("booking.Guest_Email"), field: "guestEmail" },
                        { label: this.$t("booking.Guest_Contact"), field: "guestContact" },
                        { label: this.$t("properties_list.Property_Type"), field: "propertyType" },
                        { label: this.$t("dashboard.refund_type"), field: "refundType" },
                        { label: this.$t("dashboard.refund_amount"), field: "refundAmount" },
                        { label: this.$t("dashboard.refund_status"), field: "status" },
                        { label: this.$t("dashboard.scheduled_at"), field: "scheduledAt" },
                    ];
                case 2:
                    return [
                        { label: this.$t("booking.Guest"), field: "guestName" },
                        { label: this.$t("booking.Guest_Email"), field: "guestEmail" },
                        { label: this.$t("properties_list.Property_Type"), field: "propertyType" },
                        { label: this.$t("dashboard.deposit_amount"), field: "depositAmount" },
                        { label: this.$t("dashboard.deposit_status"), field: "depositStatus" },
                        { label: this.$t("dashboard.claim_window_ends"), field: "claimWindowEnds" },
                        { label: this.$t("payment.damage_claim_reason"), field: "claimReason" },
                        { label: this.$t("properties_list.action"), field: "action" },
                    ];
                default:
                    return [];
            }
        },
        currentTabTitle() {
            const titles = [
                this.$t("dashboard.cancellations"),
                this.$t("dashboard.refunds"),
                this.$t("dashboard.security_deposits"),
            ];
            return titles[this.activeTab];
        }
    },
    methods: {
        onTabChange(index) {
            this.activeTab = index;
            this.rows = [];
            this.serverParams.page = 1;
            if (index === 2) {
                this.depositStatusFilter = '';
                delete this.serverParams.status;
            }
            this.loadItems();
        },

        onPageChange(params) {
            this.serverParams.page = params.currentPage;
            this.loadItems();
        },

        onPerPageChange(params) {
            this.serverParams.perPage = params.currentPerPage;
            this.serverParams.page = 1;
            this.loadItems();
        },

        onSearch: debounce(function (searchQuery) {
            this.serverParams.search = searchQuery;
            this.serverParams.page = 1;
            this.loadItems();
        }, 300),

        onDepositStatusFilterChange() {
            this.serverParams.page = 1;
            if (this.depositStatusFilter) {
                this.serverParams.status = this.depositStatusFilter;
            } else {
                delete this.serverParams.status;
            }
            this.loadItems();
        },

        loadItems() {
            this.isLoading = true;

            const flattenRow = (row) => {
                let base = { ...row };

                if (this.activeTab === 1) {
                    // For Refunds, guest and property info are nested under payment
                    base.guestName = row.payment?.user?.name || '';
                    base.guestEmail = row.payment?.user?.email || '';
                    base.guestContact = row.payment?.user?.mobile_phone || '';
                    base.propertyType = row.payment?.property?.property_type || '';
                    // base.refundType = row.refund_type ? row.refund_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : '-';
                    base.refundType = row.refund_type
                        ? `${row.refund_type.charAt(0).toUpperCase() + row.refund_type.slice(1).toLowerCase()}_refund`
                        : 'No_refund';

                    // base.refundAmount = row.refund_amount || 0;
                    base.refundAmount = row.refund_amount ? this.formatCurrency(row.refund_amount, row.payment?.payment_currency) : '-';
                    // base.status = row.status ? row.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : '-';
                    base.status = row.status
                        ? row.status.trim().charAt(0).toUpperCase() + row.status.trim().slice(1).toLowerCase()
                        : 'Pending';

                    base.scheduledAt = row.scheduled_at ? moment(row.scheduled_at).format('DD-MM-YYYY HH:mm') : '-';
                }
                if (this.activeTab === 2) {
                    const payment = row.payment || {};
                    const currency = payment.payment_currency || row.currency || 'AED';
                    base.guestName = payment.user?.name || '';
                    base.guestEmail = payment.user?.email || '';
                    base.propertyType = payment.property?.property_type || '';
                    base.depositAmount = `${currency} ${row.amount}`;
                    base.depositStatusLabel = this.formatDepositStatus(row.status);
                    base.depositStatusClass = this.depositStatusClass(row.status);
                    base.claimWindowEnds = row.claim_window_ends_at ? moment(row.claim_window_ends_at).format('DD-MM-YYYY HH:mm') : '-';
                    base.claimReason = row.host_claim_reason || '-';
                    base.claimDescription = row.host_claim_description || '';
                    base.paymentId = row.payment_id;
                    base.depositAmountValue = parseFloat(row.amount) || 0;
                    base.canResolve = row.status === 'host_claimed';
                }
                if (this.activeTab === 0) {
                    // For Cancellations, guest and property info are at the top level
                    base.cancellation_reason = row.cancellation_reason || '-';
                    base.cancellation_date_time = row.cancellation_date_time ? moment(row.cancellation_date_time).format('DD-MM-YYYY HH:mm') : '-';
                    base.guestName = row.user?.name || '';
                    base.guestEmail = row.user?.email || '';
                    base.guestContact = row.user?.mobile_phone || '';
                    base.propertyType = row.property?.property_type || '';
                }

                return base;
            };
            const onSuccess = (data) => {
                const fetchedRows = data.data || [];
                this.rows = fetchedRows.map(flattenRow);
                this.totalRecords = data.totalRecords || 0;
                if (this.activeTab === 2 && data.stats) {
                    this.depositStats = data.stats;
                }
                this.noDataFound = this.rows.length === 0;
                this.isLoading = false;
            };

            const onError = (err) => {
                console.error('Failed to load data', err);
                this.isLoading = false;
                this.$showNotification({ message: this.$t('errors.failed_load_data') }, "error");
            };

            // Fetch data based on active tab
            switch (this.activeTab) {
                case 0: // Cancellations
                    API.getAllCancellations(this.serverParams, onSuccess, onError);
                    break;
                case 1: // Refunds
                    API.getAllRefunds(this.serverParams, onSuccess, onError);
                    break;
                case 2:
                    API.getSecurityDepositClaims(this.serverParams, onSuccess, onError);
                    break;
            }
        },
        openResolveModal(row) {
            const depositMax = Math.round((parseFloat(row.depositAmountValue) || 0) * 100) / 100;
            this.resolveAmountError = '';
            this.resolveForm = {
                paymentId: row.paymentId,
                depositAmountLabel: row.depositAmount,
                depositMaxAmount: depositMax,
                claimReason: row.claimReason !== '-' ? row.claimReason : '',
                claimDescription: row.claimDescription || '',
                guest_refund_amount: depositMax,
                host_release_amount: 0,
                admin_notes: '',
            };
            this.$refs.resolveDepositModal.show();
        },
        clampResolveAmount(value) {
            const max = Number(this.resolveForm.depositMaxAmount) || 0;
            let amount = Number(value);
            if (Number.isNaN(amount) || amount < 0) amount = 0;
            if (amount > max) amount = max;
            return Math.round(amount * 100) / 100;
        },
        onResolveGuestAmountInput() {
            this.resolveForm.guest_refund_amount = this.clampResolveAmount(this.resolveForm.guest_refund_amount);
            this.validateResolveAmounts(true);
        },
        onResolveHostAmountInput() {
            this.resolveForm.host_release_amount = this.clampResolveAmount(this.resolveForm.host_release_amount);
            this.validateResolveAmounts(true);
        },
        validateResolveAmounts(showError = true) {
            const max = Number(this.resolveForm.depositMaxAmount) || 0;
            const guest = this.clampResolveAmount(this.resolveForm.guest_refund_amount);
            const host = this.clampResolveAmount(this.resolveForm.host_release_amount);
            this.resolveForm.guest_refund_amount = guest;
            this.resolveForm.host_release_amount = host;

            if (guest > max || host > max) {
                if (showError) {
                    this.resolveAmountError = this.$t('dashboard.deposit_amount_exceeds_limit', {
                        amount: this.resolveForm.depositAmountLabel,
                    });
                }
                return false;
            }

            if (Math.abs((guest + host) - max) > 0.01) {
                if (showError) {
                    this.resolveAmountError = this.$t('dashboard.deposit_split_must_equal_total', {
                        amount: this.resolveForm.depositAmountLabel,
                    });
                }
                return false;
            }

            this.resolveAmountError = '';
            return true;
        },
        submitResolveDeposit() {
            if (!this.resolveForm.paymentId || this.resolveSubmitting) return;
            if (!this.validateResolveAmounts(true)) return;
            const paymentId = this.resolveForm.paymentId;
            this.resolveSubmitting = true;
            this.isLoading = true;
            API.adminResolveSecurityDeposit(paymentId, {
                guest_refund_amount: this.resolveForm.guest_refund_amount,
                host_release_amount: this.resolveForm.host_release_amount,
                admin_notes: this.resolveForm.admin_notes,
            }, (data) => {
                if (data.status == 200) {
                    this.resolveAmountError = '';
                    this.markDepositResolvedInTable(paymentId);
                    this.$refs.resolveDepositModal.hide();
                    this.$showNotification({ message: this.$t('dashboard.deposit_resolved') }, 'success');
                    this.resolveSubmitting = false;
                    this.tableRefreshKey += 1;
                    this.loadItems();
                } else {
                    this.resolveSubmitting = false;
                    this.isLoading = false;
                    this.resolveAmountError = data.message || this.$t('errors.failed_load_data');
                    this.$showNotification({ message: this.resolveAmountError }, 'error');
                }
            }, (error) => {
                this.resolveSubmitting = false;
                this.isLoading = false;
                this.resolveAmountError = error?.data?.message
                    || error?.message
                    || this.$t('errors.failed_load_data');
                this.$showNotification({ message: this.resolveAmountError }, 'error');
            });
        },
        markDepositResolvedInTable(paymentId) {
            this.rows = this.rows.map((row) => {
                if (row.paymentId !== paymentId) return row;
                return {
                    ...row,
                    status: 'resolved',
                    depositStatusLabel: this.formatDepositStatus('resolved'),
                    depositStatusClass: this.depositStatusClass('resolved'),
                    canResolve: false,
                };
            });
            this.depositStats = {
                ...this.depositStats,
                disputed_count: Math.max(0, (Number(this.depositStats.disputed_count) || 0) - 1),
                resolved_count: (Number(this.depositStats.resolved_count) || 0) + 1,
            };
        },
        formatCurrency(amountInCents, currency = 'AED') {
            if (!amountInCents || isNaN(amountInCents)) return '-';
            const amount = amountInCents / 100;
            const formattedAmount = Number(amount).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
            return `${currency.toUpperCase()} ${formattedAmount}`;
        },
        formatStatAmount(amount) {
            if (amount === null || amount === undefined) return '-';
            return Number(amount).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        },
        formatDepositStatus(status) {
            if (!status) return '-';
            return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
        },
        depositStatusClass(status) {
            const map = {
                held: 'badge bg-info text-white',
                awaiting_release: 'badge bg-primary text-white',
                host_claimed: 'badge bg-danger text-white',
                refund_pending: 'badge bg-warning text-dark',
                refunded: 'badge bg-secondary text-white',
                resolved: 'badge bg-success text-white',
            };
            return map[status] || 'badge bg-secondary text-white';
        },

        bookingDetail(id) {
            this.$router.push({ name: 'booking_detail', params: { id } });
        },

        propertyDetail(id) {
            this.$router.push({ name: 'properties_detail', params: { id } });
        },
        handleApiError(err) {
            console.error(err);
            this.$swal({
                title: 'Error',
                type: 'error',
                text: this.$t('errors.failed_load_data'),
                icon: 'error'
            });
        },
        updateParams(newProps) {
            this.serverParams = Object.assign({}, this.serverParams, newProps);
        },
        formatDate(date) {
            return moment(date).format('DD-MM-YYYY');
        },
        errorMsg(err) {
            console.log(err);
            this.$showNotification({ message: 'Failed system error' }, "error")
        }
    },
    watch: {
        "$i18n.locale"(newVal, oldVal) {
            this.loadItems();
        }
    },
    mounted() {
        this.isLoading = true;
        Vue.prototype.$EventBus.$on('changeLanguage', this.updateColumnLabels);
        this.loadItems();
        this.updateColumnLabels();
    }
};
</script>


<style scoped>
button.btn.new-primary-outline-btn, button.btn.new-primary-btn {
    padding: 10px !important;
    border-radius: 5px !important;
    font-size: 16px !important;
    line-height: 1 !important;
    height: max-content !important;
}
button.btn.new-primary-btn {
    background: #70D3FF !important;
    border-color: #70D3FF !important;
    color: #fff !important;
}
button.btn.new-primary-btn:hover {
    background: #fff !important;
    color: #70D3FF !important;
    border-color: #70D3FF !important;
}
html body button.btn.new-primary-outline-btn {
    color: #70D3FF !important;
    background-color: #fff !important;
}
html body button.btn.new-primary-outline-btn:hover {
    background: #70D3FF !important;
    color: #fff !important;
    border-color: #70D3FF !important;
}
.deposit-stat-card {
    background: #f8fbff;
    border: 1px solid #d9ecff;
    border-radius: 10px;
    padding: 16px;
    height: 100%;
}

.deposit-stat-label {
    font-size: 13px;
    color: #666;
    margin-bottom: 6px;
}

.deposit-stat-value {
    font-size: 24px;
    font-weight: 700;
    color: #191e3b;
}

.deposit-stat-sub {
    font-size: 13px;
    color: #70D3FF;
    margin-top: 4px;
}

.claim-details-box {
    background: #f8fbff;
    border: 1px solid #d9ecff;
    border-radius: 8px;
    padding: 12px 14px;
}

.claim-description-text {
    margin-top: 6px;
    white-space: pre-wrap;
    word-break: break-word;
    color: #333;
    line-height: 1.5;
}

.resolve-submit-status {
    background: #f7fafc;
    border-radius: 8px;
    padding: 12px;
}

.spinner-inline {
    width: 28px;
    height: 28px;
    border: 3px solid #f3f3f3;
    border-top: 3px solid #70D3FF;
    border-radius: 50%;
    animation: resolve-spin 1s linear infinite;
}

.spinner-btn {
    display: inline-block;
    width: 14px;
    height: 14px;
    border: 2px solid #f3f3f3;
    border-top: 2px solid #fff;
    border-radius: 50%;
    vertical-align: -2px;
    animation: resolve-spin 1s linear infinite;
}

@keyframes resolve-spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}
</style>
<style>
.nav-tabs .nav-item .nav-link.active {
    border-radius: 0;
    background: #70D3FF;
    border: 1px solid #70D3FF;
    color: #fff;
}

.card-header .nav-tabs {
    display: flex;
    margin-bottom: 0;
}

.nav-tabs .nav-item .nav-link {
    border: 1px solid #dcdfe6;
    font-weight: bold;
}

.tab-content {
    display: none;
}

.vgt-global-search {
    padding: 6px;
    border-radius: 10px;
    width: 100%;
    max-width: 275px;
    border: 1px solid rgb(220, 223, 230);
    background: #fff;
    margin-bottom: 15px;
}

.vgt-global-search input {
    border: 0;
}

.sidebar .collapse.show ul.nav.flex-column.sub-menu {
    padding: 0 !important;
    list-style: none !important;
    border-radius: 0 0 10px 10px;
    border-top: 1px solid #eee;
}

.custom-table {
    table-layout: auto !important;
    width: 100%;
    word-wrap: break-word;
}

.vgt-table td,
.vgt-table th {
    vertical-align: middle;
    white-space: nowrap;
}

.vgt-table td span {
    max-width: 200px;
    overflow: hidden;
    white-space: nowrap;
}

.card-body {
    overflow-x: auto;
}

@media (max-width: 499px) {
    .card-header .nav-tabs .nav-item {
        width: 100%;
    }
}
</style>